import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../../backend/amplify/data/resource';

/** Intervalle entre deux pulsations, tant que l'onglet est visible. */
const PING_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Deux pulsations plus rapprochées que ça sont ignorées.
 *
 * Le retour sur l'onglet déclenche une pulsation immédiate, qui peut tomber à
 * quelques millisecondes du minuteur. Le SQL du serveur est écrit en une seule
 * instruction pour que deux appels concurrents ne créent pas deux sessions,
 * mais rien ne le garantit sous `READ COMMITTED` : ce garde-fou-ci ferme le cas
 * réaliste à la source. Le coût d'une course perdue resterait faible — une
 * ligne de session en trop, jamais une durée fausse.
 */
const MIN_GAP_MS = 60 * 1000;

/**
 * Écrans où l'on ne pulse pas. Un compte qui atterrit sur `/no-access` est
 * authentifié mais n'a aucun espace : le compter présent le ferait passer pour
 * un utilisateur actif, ce que l'écran de suivi doit précisément savoir
 * détecter. Le serveur refuse de toute façon une identité sans aucun groupe.
 */
const EXCLUDED_ROUTES = ['/login', '/onboarding', '/no-access'];

/**
 * Pulsation de présence : dit au serveur « je suis là, maintenant ».
 *
 * POURQUOI PAS POSTHOG, QUI MESURE DÉJÀ DES SESSIONS. Son proxy
 * (`glo.latouretpetit.link`) pointe par CNAME vers `europehog.com` : uBlock
 * Origin démasque les CNAME sur Firefox, et le TLD `.link` est souvent filtré
 * par les DNS d'entreprise. Rien de tout ça n'est certain, et rien n'est
 * observable depuis l'application — la mesure disparaîtrait en silence. Or un
 * écran qui répond « est-ce qu'ils utilisent l'app » vaut par ses zéros, et le
 * biais frappe justement les utilisateurs les mieux outillés.
 *
 * Cette pulsation emprunte l'API de l'application elle-même : exactement le
 * chemin qui charge chaque écran. S'il était bloqué, l'application ne
 * fonctionnerait pas du tout.
 *
 * Elle NE MESURE PAS un temps de travail. Voir migration 0096.
 */
@Injectable({ providedIn: 'root' })
export class SessionPingService {

  // `generateClient<Schema>()` et non `as any` : le champ GraphQL d'une requête
  // portée par une Lambda porte le nom de la constante exportée, SUFFIXE
  // COMPRIS — `manageUserSessionsQuery`. Un nom fautif a déjà coûté trois
  // semaines d'échecs silencieux sur `manageAuditedDeletes` parce que le client
  // non typé ne vérifie rien. Ici, il casse la compilation.
  private readonly client = generateClient<Schema>();
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);

  private timer: ReturnType<typeof setInterval> | null = null;
  private onVisibility: (() => void) | null = null;
  private lastPingAt = 0;

  /** Démarre la pulsation. Idempotent : un second appel ne double pas le minuteur. */
  start(): void {
    if (this.timer) return;

    // Hors zone Angular, comme `PosthogService` : sinon c'est un cycle de
    // détection de changements toutes les cinq minutes, pour un appel dont
    // aucune vue ne dépend.
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => this.ping(), PING_INTERVAL_MS);

      // Le retour sur l'onglet pulse tout de suite : sans ça, un retour après
      // 40 minutes n'ouvrirait la nouvelle session qu'au prochain tic, soit
      // jusqu'à cinq minutes de présence perdues à chaque reprise.
      this.onVisibility = () => {
        if (document.visibilityState === 'visible') this.ping();
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      this.ping();
    });
  }

  /** Arrête la pulsation (déconnexion). La session en cours se ferme d'elle-même. */
  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.onVisibility) {
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.onVisibility = null;
    }
    this.lastPingAt = 0;
  }

  private async ping(): Promise<void> {
    // Un onglet masqué n'est pas de la présence. C'est la seule différence
    // entre mesurer l'usage et mesurer les onglets oubliés.
    if (document.visibilityState !== 'visible') return;
    if (EXCLUDED_ROUTES.some(route => this.router.url.startsWith(route))) return;

    const now = Date.now();
    if (now - this.lastPingAt < MIN_GAP_MS) return;
    this.lastPingAt = now;

    try {
      await this.client.queries.manageUserSessionsQuery({ action: 'ping' });
    } catch {
      // Silencieux, et réessayé au tic suivant : la mesure ne doit jamais
      // gêner l'application. Une pulsation perdue coûte au pire cinq minutes
      // de présence non comptées.
      this.lastPingAt = 0;
    }
  }
}
